/**
 * German copy for the refusals the **account-administration** actions can answer with — linking and unlinking
 * an external authenticator, switching the password login on and off, and starting an impersonation.
 *
 * ## Why one module for three screens
 *
 * The same two account-safety rules are enforced on three different surfaces, each acting on a different kind
 * of account through a different endpoint but hitting the identical `ExternalIdentityService` guard:
 *
 *  * `_modules/company/employee-details` — an administrator managing an **employee's** authenticators;
 *  * `_modules/employee-panel/my-profile` — an employee managing **their own**;
 *  * `owner/user` — a platform owner managing a **management user's**, and impersonating one;
 *  * `_modules/owner/account-administration` — a platform owner resetting somebody's **second factor**
 *    (#279/#282). A fourth surface, added for the same reason the other three share this table: its two
 *    refusals are read by an operator who has to decide what to do next, and a developer-facing English
 *    sentence on a German screen is not a decision anybody can act on.
 *
 * All three used to read the server's `ApiError.description`, which is developer-facing English by design (it
 * comes from `ErrorCatalog`, for Postman, logs and docs) — so a rule somebody has to act on arrived in the
 * wrong language on a German screen, which is the worst kind of sentence to leave untranslated. See the
 * frontend style guide, *"Surfacing an API error — branch on `code`, never render `description`"*.
 *
 * They share a table rather than each carrying a copy, because three copies of one rule is three chances for
 * the wording to drift, and an administrator and the account holder must read the *same* rule.
 *
 * > **Known second copy:** `ProfileComponent.actionMessageFor`
 * > (`authentication/profile/profile.component.ts`) holds the same two account-safety sentences for the
 * > platform-account profile. It was fixed first, before this module existed. Fold it in here the next time
 * > that file is open — it also maps `E_AUTH_ACCOUNT_DEACTIVATED`, which is reachable from the claim flow it
 * > serves but from none of the three endpoints above.
 *
 * ## What to do when a new code appears
 *
 * Add it here with its own German sentence, read from that code's XML doc in
 * `API.Core/Authorization/AuthErrorCodes.cs` — the catalog description is a one-line summary, the doc comment
 * above the registration is what the sentence needs to say. Do **not** widen
 * {@link describeAccountActionError} back to `description`, and do not make the fallback more specific.
 */

import {readErrorCode} from '../../_auth/two-factor/second-factor-errors';
import {DEMO_COMPANY_LINK_HINT, DemoCompanyErrorCodes} from './demo-company-errors';

/**
 * `ApiError.code` → German sentence.
 *
 * Deliberately short. These endpoints answer **bare** statuses for almost everything — an unknown employee, a
 * missing identity, a missing company claim and every `[Authorize]` refusal all come back as a plain
 * 400/401/403/404 with no `ApiError` body at all (`ResponseModelExtensions.BuildResponse` only serialises the
 * structured body when `ErrorCode is not null`). The codes below are the complete set of *structured* refusals
 * these six endpoints can produce; everything else is the caller's fallback, by construction rather than by
 * omission.
 */
const ACCOUNT_ACTION_MESSAGES: Readonly<Record<string, string>> = {
  // ----- the two account-safety rules (ExternalIdentityService) -----
  //
  // They are mirror images of one guard: an account must never end up with no working door. Both name the
  // remedy, because neither is discoverable from the toggle that was just refused.

  /** Removing the last external authenticator while the password login is off. */
  E_AUTH_LAST_EXTERNAL_AUTH_REMOVAL_BLOCKED:
    'Die letzte externe Anmeldung kann nicht entfernt werden, solange die Passwort-Anmeldung abgeschaltet ' +
    'ist. Schalten Sie diese zuerst wieder ein.',
  /** Switching the password login off with no external authenticator active. */
  E_AUTH_PASSWORD_DISABLE_REQUIRES_EXTERNAL:
    'Die Passwort-Anmeldung lässt sich nur abschalten, solange mindestens eine externe Anmeldung (Microsoft, ' +
    'Google oder Apple) aktiv ist. Verknüpfen Sie zuerst eine.',

  // ----- demo companies (#934) -----

  /** Linking a Microsoft, Google or Apple account to a user or employee of a demo company. */
  [DemoCompanyErrorCodes.IdentityLinkRefused]: DEMO_COMPANY_LINK_HINT,

  // ----- impersonation (ImpersonationService, platform owner only) -----

  /** Already impersonating. Nesting is refused outright, so the remedy is to end the current session first. */
  E_IMPERSONATION_NESTED:
    'Sie arbeiten bereits als ein anderes Konto. Beenden Sie diese Sitzung zuerst über das Banner am unteren ' +
    'Bildschirmrand.',
  /** The target row was gone by the time the request arrived — almost always a stale list. */
  E_IMPERSONATION_TARGET_NOT_FOUND:
    'Dieses Benutzerkonto wurde nicht gefunden — vermutlich wurde es zwischenzeitlich gelöscht. Bitte laden ' +
    'Sie die Seite neu.',
  /** A platform owner may not be impersonated at all. */
  E_IMPERSONATION_PLATFORMOWNER_FAILED:
    'Ein Plattforminhaber-Konto kann nicht übernommen werden.',
  // Defence in depth: `[Authorize(Roles = PlatformOwner)]` on ImpersonationController already answers a bare
  // 403 before the service runs, so this code should be unreachable over HTTP. Mapped anyway — an unreachable
  // code costs one line, whereas the day the gate moves is not the day to discover the sentence is missing.
  E_IMPERSONATION_FORBIDDEN:
    'Nur Plattforminhaber dürfen ein anderes Konto übernehmen.',

  // ----- the administrative second-factor reset (AccountSecondFactorService, platform owner only) -----
  //
  // Reached only from the owner screen `_modules/owner/account-administration`. They are two codes rather than
  // one because the reader has to be able to tell WHICH kind of stale the row was: the account is gone, or
  // somebody got there first. Deliberately NOT added to `ProfileComponent.actionMessageFor` — that switch
  // serves the platform-account profile, which has no administrative reset and can never answer either code.

  /** The identity was gone by the time the reset arrived — a deleted account, or a stale listing. */
  E_AUTH_ACCOUNT_IDENTITY_NOT_FOUND:
    'Dieses Konto wurde nicht gefunden — vermutlich wurde es zwischenzeitlich gelöscht. Die Liste wurde neu ' +
    'geladen.',
  /** No confirmed authenticator, so there was nothing to remove. Not a mistake the operator made. */
  E_AUTH_SECOND_FACTOR_NOTHING_TO_RESET:
    'Dieses Konto hat keine Zwei-Faktor-Anmeldung eingerichtet, es war also nichts zurückzusetzen. ' +
    'Möglicherweise war die Liste nicht mehr aktuell.'
};

/**
 * The German sentence for a failed account-administration action.
 *
 * @param err      whatever rxjs handed the `error` callback.
 * @param fallback the German sentence for a failure this table does not recognise. Per call site rather than
 *                 one global string, because the bare-status paths are the *common* case here (see
 *                 {@link ACCOUNT_ACTION_MESSAGES}) and "Die Aktion konnte nicht ausgeführt werden." is worth
 *                 rather less than "Impersonation fehlgeschlagen. Bitte erneut versuchen." on the screen that
 *                 says it. It must stay a fixed German sentence: a code that ships without a mapping degrades
 *                 to it, never to `description`.
 */
export function describeAccountActionError(err: unknown, fallback: string): string {
  const code = readErrorCode(err);
  return (code && ACCOUNT_ACTION_MESSAGES[code]) || fallback;
}
